import { useState, useMemo } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

// Null/undefined/empty-string handling: such values always sort to the bottom,
// regardless of ascending/descending direction (per spec §8).
const isEmpty = (v) => v === null || v === undefined || v === "";

// Default comparator. Handles numbers, dates (ISO strings), strings, booleans.
// Returns a value suitable for [a,b].sort().
const compare = (a, b) => {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return 1;   // empties always at bottom
  if (isEmpty(b)) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a === b) ? 0 : a ? -1 : 1;
  const an = Number(a), bn = Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn) && (typeof a !== "string" || /^-?\d+(\.\d+)?$/.test(a))) {
    return an - bn;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

// Row getter helpers for common accessor forms: dotted path string or function(row)->value.
const getValue = (row, accessor) => {
  if (typeof accessor === "function") return accessor(row);
  if (typeof accessor === "string") {
    return accessor.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), row);
  }
  return undefined;
};

/**
 * useTableSort — 3-state header-click cycle (asc → desc → none → asc → …).
 *
 * @param {Array<object>} rows                 — filtered rows to sort.
 * @param {Object}        opts
 * @param {string}        opts.defaultKey      — column key active on initial load.
 * @param {'asc'|'desc'}  [opts.defaultOrder]  — direction of the default sort.
 * @param {Object<string,Function|string>} opts.accessors — per-column value getters (fn or dotted-path).
 *
 * Returns { sortedRows, sort, toggleSort, isActive, indicator }
 */
export function useTableSort(rows, { defaultKey, defaultOrder = "asc", accessors = {} } = {}) {
  const [sort, setSort] = useState({ key: defaultKey || null, order: defaultKey ? defaultOrder : null });

  const toggleSort = (key) => {
    setSort((cur) => {
      if (cur.key !== key) return { key, order: "asc" };
      if (cur.order === "asc") return { key, order: "desc" };
      if (cur.order === "desc") {
        // Third click → reset (no active sort). Fall back to default if configured.
        if (defaultKey && defaultKey !== key) return { key: defaultKey, order: defaultOrder };
        return { key: null, order: null };
      }
      return { key, order: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    if (!sort.key || !sort.order) return rows;
    const accessor = accessors[sort.key] || sort.key;
    const dir = sort.order === "desc" ? -1 : 1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = getValue(a, accessor);
      const bv = getValue(b, accessor);
      // Empty values stay at bottom regardless of direction (spec §8).
      if (isEmpty(av) && isEmpty(bv)) return 0;
      if (isEmpty(av)) return 1;
      if (isEmpty(bv)) return -1;
      return compare(av, bv) * dir;
    });
    return copy;
  }, [rows, sort, accessors]);

  const isActive = (key) => sort.key === key;
  const indicator = (key) => (sort.key === key ? sort.order : null);

  return { sortedRows, sort, toggleSort, isActive, indicator };
}

/**
 * SortableHeader — drop-in replacement for shadcn <TableHead> when a column is sortable.
 * Renders header text + a directional arrow.
 *
 * Usage:
 *   <SortableHeader sortKey="seller_name" sort={sort} onToggle={toggleSort}>Seller Name</SortableHeader>
 */
export function SortableHeader({ sortKey, sort, onToggle, className = "", children, align = "left", ...rest }) {
  const active = sort?.key === sortKey && !!sort?.order;
  const order = active ? sort.order : null;
  const Icon = order === "asc" ? ArrowUp : order === "desc" ? ArrowDown : ArrowUpDown;
  const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <th
      scope="col"
      {...rest}
      aria-sort={order === "asc" ? "ascending" : order === "desc" ? "descending" : "none"}
      className={`h-10 px-4 text-left align-middle text-muted-foreground [&:has([role=checkbox])]:pr-0 select-none ${className}`}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1.5 text-inherit font-semibold text-slate-700 hover:text-[#1D9E75] transition-colors ${justify} w-full`}
        data-testid={`sort-header-${sortKey}`}
        data-active={active ? "true" : "false"}
      >
        <span className={active ? "text-[#1D9E75]" : ""}>{children}</span>
        <Icon
          className={`h-3.5 w-3.5 flex-shrink-0 ${active ? "text-[#1D9E75]" : "text-slate-400"}`}
          strokeWidth={active ? 2.5 : 2}
        />
      </button>
    </th>
  );
}
